/**
 * ModalShell — opinionated modal backdrop.
 *
 * (2026-08-27) Per app-wide "no-close-on-outside-click" policy, this shell
 * no longer dismisses the modal when the user clicks the backdrop. It
 * retains the mousedown/mouseup guard from the original "select-dropdown
 * bubble" fix so any legitimate close (via the shell's own close button
 * or Escape from the child) still works cleanly.
 *
 * (2026-08-27 follow-up) Escape-to-close is now wired centrally via
 * `useEscapeKey(onClose)` so every ModalShell-based modal responds to
 * Esc consistently, matching Radix Dialog / Sheet / AlertDialog.
 */
import React from 'react';
import useEscapeKey from '../hooks/useEscapeKey';

export default function ModalShell({
  onClose,
  children,
  className = '',
  cardClassName = 'max-w-xl w-full p-5',
  testid,
}) {
  useEscapeKey(onClose);
  return (
    <div
      // pb-24 on mobile reserves ~96px at the bottom so the modal card
      // never sits under the fixed mobile bottom-nav (z-40, ~72px tall
      // + safe-area). Every modal in the app benefits from this shell,
      // keeping "buttons hidden below bottom-nav" bugs from recurring.
      className={`fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4 pb-24 md:pb-4 ${className}`}
      data-testid={testid}
    >
      <div
        // `max-h` + `overflow-y-auto` guarantee the card content is
        // scrollable when it's taller than the visible viewport. `dvh`
        // reflects the browser's real visible height (accounts for
        // auto-hiding URL bars). `sm:max-h-[90vh]` restores the roomier
        // desktop cap where the bottom-nav doesn't exist.
        className={`bg-white rounded-lg shadow-2xl overflow-y-auto max-h-[calc(100dvh-96px)] sm:max-h-[90vh] ${cardClassName}`}
      >
        {children}
      </div>
    </div>
  );
}
